import Cropper, { Area } from "react-easy-crop";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/lib/components/Button/Button";
import { IconReload, IconX } from "@tabler/icons-react";
import { CropperState } from "@/lib/stores/editor-store/editor-state";
import { getCompositionTimings } from "@repo/video-engine-i2v/utils/timing";
import { getReverseTrimBefore } from "@repo/video-engine-i2v/components/AnimatedImage";
import { CompositionDto } from "@repo/api/editor";
import { Slider } from "@/lib/components/Slider";
import { AnimatedImageDto } from "@repo/api/animated-image";

type Props = {
  cropperState: CropperState;
};

// VideoCropper guards that VideoCropperInternal is not rendered
// if the animated image is not selected in the composition anymore.
export default function VideoCropper({ cropperState }: Props) {
  const composition = useEditorStore((state) => state.video!.compositionJson);
  const setCropperState = useEditorStore(
    (state) => state.service.setCropperState,
  );
  const isAnimatedImagePresent = useMemo(() => {
    return !!composition.mainSceneTimeline.find(
      (item) =>
        item.externalId === cropperState.timelineItemExternalId &&
        item.animatedImageExternalId === cropperState.animatedImageExternalId,
    );
  }, [composition, cropperState]);

  useEffect(() => {
    if (!isAnimatedImagePresent) {
      setCropperState(null);
    }
  }, [isAnimatedImagePresent, setCropperState]);

  if (!isAnimatedImagePresent) {
    return null;
  }

  return <VideoCropperInternal cropperState={cropperState} />;
}

function VideoCropperInternal({ cropperState }: Props) {
  const composition = useEditorStore((state) => state.video!.compositionJson);
  const animatedImages = useEditorStore((state) => state.animatedImages);
  const updateVideo = useEditorStore((state) => state.service.updateVideo);
  const setCropperState = useEditorStore(
    (state) => state.service.setCropperState,
  );
  const getCurrentFrame = useEditorStore(
    (state) => state.service.getCurrentFrame,
  );
  const setCurrentFrame = useEditorStore(
    (state) => state.service.setCurrentFrame,
  );
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);

  const animatedImage = animatedImages[cropperState.animatedImageExternalId];

  const config = useMemo(() => {
    const item = composition.mainSceneTimeline.find(
      (item) => item.externalId === cropperState.timelineItemExternalId,
    );

    const config =
      item?.animatedImageConfigs[cropperState.animatedImageExternalId];

    if (!config) {
      throw new Error("Crop config not found");
    }

    return config;
  }, [composition.mainSceneTimeline, cropperState]);

  const initialCropArea = config.cropConfigs[composition.aspectRatio];

  const cropArea = useRef<Area | null>(initialCropArea ?? null);

  const onCropAreaChange = (value: Area) => {
    cropArea.current = value;
  };

  const aspectRatioParts = composition.aspectRatio
    .split(":")
    .map((part) => parseInt(part));

  const minZoom = 1;
  const maxZoom = 2;

  return (
    <div className="flex h-full w-full flex-col justify-center gap-4 px-10 py-10">
      <div className="relative flex-1 self-stretch overflow-hidden">
        <Cropper
          classes={{
            containerClassName: "rounded-lg",
          }}
          video={
            config.reverse
              ? animatedImage?.backwardSrc
              : animatedImage?.forwardSrc
          }
          crop={crop}
          initialCroppedAreaPercentages={initialCropArea}
          zoom={zoom}
          minZoom={minZoom}
          maxZoom={maxZoom}
          aspect={aspectRatioParts[0]! / aspectRatioParts[1]!}
          onCropChange={setCrop}
          onCropAreaChange={onCropAreaChange}
          onZoomChange={setZoom}
          objectFit="contain"
          showGrid={false}
          setVideoRef={(ref) => {
            if (!ref.current) {
              return;
            }

            ref.current.autoplay = false;
            ref.current.pause();

            const initialGlobalCurrentFrame = getCurrentFrame();

            const { cropperVideoTime, globalCurrentFrame } =
              calculateCurrentTime(
                composition,
                animatedImages,
                cropperState,
                initialGlobalCurrentFrame,
              );

            ref.current.currentTime = cropperVideoTime;

            if (globalCurrentFrame !== initialGlobalCurrentFrame) {
              setCurrentFrame(globalCurrentFrame);
            }
          }}
        />
        <Button
          className="absolute top-2 right-2"
          variant="outline"
          size="iconSm"
          onClick={() => {
            setCropperState(null);
          }}
        >
          <IconX />
        </Button>
      </div>

      <div className="shrink-0 grow-0 basis-auto">
        <div className="flex h-16 items-center justify-center gap-2 rounded-2xl bg-white shadow-card">
          <p className="text-sm font-medium">Zoom</p>
          <div className="w-2.5"></div>
          <Slider
            value={zoom}
            onValueChange={setZoom}
            min={minZoom}
            max={maxZoom}
            step={0.001}
            classNameTrack="w-60"
          />
          <div className="w-10"></div>
          <Button
            variant="outline"
            size="icon"
            disabled={zoom === 1 && crop.x === 0 && crop.y === 0}
            onClick={() => {
              setZoom(1);
              setCrop({ x: 0, y: 0 });
            }}
          >
            <IconReload />
          </Button>
          <Button
            onClick={() => {
              updateVideo((video) => {
                const item = video.compositionJson.mainSceneTimeline.find(
                  (item) =>
                    item.externalId === cropperState.timelineItemExternalId,
                )!;

                if (cropArea.current) {
                  item.animatedImageConfigs[
                    cropperState.animatedImageExternalId
                  ]!.cropConfigs[composition.aspectRatio] = cropArea.current;
                }
              });
              setCropperState(null);
            }}
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}

type CurrentTime = {
  cropperVideoTime: number;
  globalCurrentFrame: number;
};

function calculateCurrentTime(
  composition: CompositionDto,
  animatedImages: Record<string, AnimatedImageDto>,
  cropperState: CropperState,
  initialGlobalCurrentFrame: number,
): CurrentTime {
  const timings = getCompositionTimings(composition, animatedImages).mainScene
    .compositionImageTimings;

  const timing = timings.find(
    (timing) =>
      timing.timelineItemExternalId === cropperState.timelineItemExternalId &&
      timing.animatedImageExternalId === cropperState.animatedImageExternalId,
  );

  if (!timing) {
    throw new Error("Timing not found");
  }

  const firstAllowedFrame = timing.globalStartAtFrame;

  if (initialGlobalCurrentFrame < firstAllowedFrame) {
    return {
      cropperVideoTime: 0,
      globalCurrentFrame: firstAllowedFrame,
    };
  }

  const lastAllowedFrame = timing.globalEndAtFrame;

  if (initialGlobalCurrentFrame > lastAllowedFrame) {
    return {
      cropperVideoTime: 0,
      globalCurrentFrame: timing.globalStartAtFrame,
    };
  }

  const currentFrame = initialGlobalCurrentFrame - timing.globalStartAtFrame;
  const playbackSpeed = getPlaybackSpeed(composition, cropperState);

  const reverseTrimFrames = getReverseTrimFrames(
    composition,
    animatedImages,
    cropperState,
  );

  const cropperVideoTime =
    (currentFrame / composition.fps) * playbackSpeed +
    reverseTrimFrames / composition.fps;

  return {
    cropperVideoTime,
    globalCurrentFrame: initialGlobalCurrentFrame,
  };
}

function getReverseTrimFrames(
  composition: CompositionDto,
  animatedImages: Record<string, AnimatedImageDto>,
  cropperState: CropperState,
): number {
  const timelineItem = composition.mainSceneTimeline.find(
    (item) => item.externalId === cropperState.timelineItemExternalId,
  );

  const config =
    timelineItem?.animatedImageConfigs[cropperState.animatedImageExternalId];

  if (!config?.reverse) {
    return 0;
  }

  const animatedImage = animatedImages[cropperState.animatedImageExternalId];

  if (!animatedImage) {
    return 0;
  }

  return getReverseTrimBefore(animatedImage, config, composition.fps) ?? 0;
}

function getPlaybackSpeed(
  composition: CompositionDto,
  cropperState: CropperState,
) {
  const timelineItem = composition.mainSceneTimeline.find(
    (item) => item.externalId === cropperState.timelineItemExternalId,
  );
  if (!timelineItem) {
    return 1;
  }

  const animatedImageConfig =
    timelineItem.animatedImageConfigs[cropperState.animatedImageExternalId];
  if (!animatedImageConfig) {
    return 1;
  }

  return animatedImageConfig.playbackSpeed ?? 1;
}
